import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Chip, Alert, IconButton, Tooltip, Collapse,
  Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSearchParams } from 'react-router-dom';
import api, { warehouseService, defectStockService } from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';
import SendToCrusherDialog from '../components/SendToCrusherDialog';
import { orderRefFromRow } from '../utils/orderLabel';

const LIST_PAGE_SIZE = 50;

/** Fallback з /warehouses/, без поточного складу (виробництво). */
const SOURCE_WAREHOUSE_ID = 'production';
const FALLBACK_TRANSFER_WAREHOUSES = [
  { id: 'production', name: 'Склад виробництва' },
  { id: 'raw', name: 'Склад сировини' },
  { id: 'dyes', name: 'Склад барвників' },
  { id: 'packaging', name: 'Склад упаковки' },
  { id: 'technical', name: 'Склад техматеріалів' },
  { id: 'semi', name: 'Склад напівфабрикатів' },
  { id: 'packaging_work', name: 'Склад для пакування' },
  { id: 'finished', name: 'Склад готової продукції' },
  { id: 'crushing_waste', name: 'Склад перемолів' },
  { id: 'blocking', name: 'Склад блокування' },
  { id: 'consumable', name: 'Склад розхідних матеріалів' },
  { id: 'defect', name: 'Склад браку' },
  { id: 'samples', name: 'Склад взірців' },
].filter((w) => w.id !== SOURCE_WAREHOUSE_ID);

const ALL_TABS = [
  { id: 'materials', label: 'Матеріали' },
  { id: 'finished', label: 'Готова продукція' },
  {
    id: 'defect',
    label: 'Брак',
    perm: 'menu.production.warehouse.defect',
    requireTrue: true,
  },
  { id: 'mixing', label: 'Заміси' },
  {
    id: 'waste',
    label: 'Відходи',
    perm: 'menu.production.warehouse.defect',
    requireTrue: true,
  },
];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || 'null');
  } catch {
    return null;
  }
}

export default function ProductionWarehousePage() {
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => readCurrentUser(), []);
  const perms = currentUser?.effective_permissions || {};

  const visibleTabs = useMemo(() => {
    return ALL_TABS.filter((t) => {
      if (!t.perm) return true;
      if (t.requireTrue) return perms[t.perm] === true;
      return perms[t.perm] !== false;
    });
  }, [perms]);

  const initialTabId = searchParams.get('tab') || 'materials';
  const initialIdx = visibleTabs.findIndex((t) => t.id === initialTabId);
  const [tab, setTab] = useState(initialIdx >= 0 ? initialIdx : 0);
  const activeTabId = visibleTabs[tab]?.id || 'materials';

  useEffect(() => {
    // Якщо право на «Брак» зняли / URL вказує на приховану вкладку — повернутись на доступну
    if (tab >= visibleTabs.length) {
      setTab(0);
    }
  }, [tab, visibleTabs.length]);

  // --- Tab 0: Materials ---
  const [whStock, setWhStock] = useState([]);
  const [stockTotal, setStockTotal] = useState(0);
  const [stockPage, setStockPage] = useState(1);
  const [movements, setMovements] = useState([]);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movementsPage, setMovementsPage] = useState(1);
  const [stockItems, setStockItems] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);

  // Transfer-in dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ stock_item: '', quantity: '' });
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState('');

  // Transfer-out dialog (A1): single material OR mixture (plastic+dye)
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnForm, setReturnForm] = useState({
    mode: 'single', // single | mix
    wh_stock_id: '',
    product: '',
    quantity: '',
    note: '',
    destination: 'raw',
  });
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnError, setReturnError] = useState('');
  const [transferWarehouses, setTransferWarehouses] = useState(FALLBACK_TRANSFER_WAREHOUSES);

  // --- Tab 1: Finished goods ---
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [fgExpand, setFgExpand] = useState({}); // key -> bool
  const [sgpDialog, setSgpDialog] = useState(null); // { mode, order, product, product_name, order_display, available, receiptId?, lines }
  const [sgpQty, setSgpQty] = useState('');
  const [sgpLoading, setSgpLoading] = useState(false);
  const [sgpError, setSgpError] = useState('');

  // --- Tab 2: Defects / Брак (A2) ---
  const [defects, setDefects] = useState([]);
  const [loadingDefects, setLoadingDefects] = useState(false);
  const [sendingWasteId, setSendingWasteId] = useState(null);
  const [crusherDialog, setCrusherDialog] = useState(null);

  // --- Tab 3: Mixing Batches / Заміси (Б2) ---
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({ product: '', quantity_kg: '', note: '' });
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [products, setProducts] = useState([]);

  // --- Tab 4: Waste lots ---
  const [wasteLots, setWasteLots] = useState([]);
  const [wasteMovements, setWasteMovements] = useState([]); // grouped rows from API
  const [loadingWaste, setLoadingWaste] = useState(false);
  const [wasteExpandedId, setWasteExpandedId] = useState(null);

  const fetchStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const [stockRes, movRes, siRes] = await Promise.all([
        api.get('/production-warehouse/stock/', {
          params: { ordering: '-updated_at', page: stockPage, page_size: LIST_PAGE_SIZE },
        }),
        api.get('/production-warehouse/movements/', {
          params: { page: movementsPage, page_size: LIST_PAGE_SIZE },
        }),
        api.get('/stock-items/'),
      ]);
      const stockData = stockRes.data;
      const movData = movRes.data;
      if (Array.isArray(stockData)) {
        setWhStock(stockData);
        setStockTotal(stockData.length);
      } else {
        setWhStock(stockData?.results ?? []);
        setStockTotal(stockData?.count ?? 0);
      }
      if (Array.isArray(movData)) {
        setMovements(movData);
        setMovementsTotal(movData.length);
      } else {
        setMovements(movData?.results ?? []);
        setMovementsTotal(movData?.count ?? 0);
      }
      setStockItems(siRes.data?.results ?? siRes.data ?? []);
    } finally {
      setLoadingStock(false);
    }
  }, [stockPage, movementsPage]);

  /** Сервер уже сортує; локальне сортування — fallback, якщо API віддав масив. */
  const sortedWhStock = useMemo(() => {
    return [...whStock].sort((a, b) => {
      const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [whStock]);

  const sortedMovements = useMemo(() => {
    return [...movements].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [movements]);

  const stockPages = Math.max(1, Math.ceil(stockTotal / LIST_PAGE_SIZE));
  const movementsPages = Math.max(1, Math.ceil(movementsTotal / LIST_PAGE_SIZE));

  const fetchReceipts = useCallback(async () => {
    setLoadingReceipts(true);
    try {
      const res = await api.get('/finished-goods-receipts/', { params: { warehouse_confirmed: false } });
      const all = res.data?.results ?? res.data ?? [];
      // Лише приходи на склад виробництва (не ГП / напівфабрикати / пакування)
      setReceipts(all.filter((r) => (r.warehouse || '') === 'Склад виробництва'));
    } finally {
      setLoadingReceipts(false);
    }
  }, []);

  const fetchDefects = useCallback(async () => {
    setLoadingDefects(true);
    try {
      const res = await api.get('/production-defect-stock/');
      setDefects(res.data?.results ?? res.data ?? []);
    } finally {
      setLoadingDefects(false);
    }
  }, []);

  const fetchBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const [batchRes, prodRes] = await Promise.all([
        api.get('/mixing-batch-stock/'),
        api.get('/products/'),
      ]);
      setBatches(batchRes.data?.results ?? batchRes.data ?? []);
      setProducts(prodRes.data?.results ?? prodRes.data ?? []);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const fetchWaste = useCallback(async () => {
    setLoadingWaste(true);
    try {
      const [lotsRes, movRes] = await Promise.all([
        api.get('/crushing-waste-lots/?ordering=-created_at&page_size=200&production_warehouse=true'),
        api.get('/production-warehouse/movements/', { params: { waste_only: true, limit: 200 } }),
      ]);
      setWasteLots(lotsRes.data?.results ?? lotsRes.data ?? []);
      setWasteMovements(movRes.data?.results ?? movRes.data ?? []);
    } finally {
      setLoadingWaste(false);
    }
  }, []);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { if (activeTabId === 'finished') fetchReceipts(); }, [activeTabId, fetchReceipts]);
  useEffect(() => { if (activeTabId === 'defect') fetchDefects(); }, [activeTabId, fetchDefects]);
  useEffect(() => { if (activeTabId === 'mixing') fetchBatches(); }, [activeTabId, fetchBatches]);
  useEffect(() => { if (activeTabId === 'waste') fetchWaste(); }, [activeTabId, fetchWaste]);

  useEffect(() => {
    warehouseService.getAll({ exclude: SOURCE_WAREHOUSE_ID })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        if (list.length) setTransferWarehouses(list);
      })
      .catch(() => {});
  }, []);

  // Transfer-in
  const handleTransferSubmit = async () => {
    setTransferError('');
    if (!transferForm.stock_item || !transferForm.quantity) {
      setTransferError('Оберіть матеріал та вкажіть кількість');
      return;
    }
    setTransferLoading(true);
    try {
      await api.post('/production-warehouse/transfer-in/', {
        stock_item: Number(transferForm.stock_item),
        quantity: Number(transferForm.quantity),
      });
      setTransferOpen(false);
      setTransferForm({ stock_item: '', quantity: '' });
      fetchStock();
    } catch (err) {
      setTransferError(err.response?.data?.error || 'Помилка при переміщенні');
    } finally {
      setTransferLoading(false);
    }
  };

  const emptyReturnForm = () => ({
    mode: 'single',
    wh_stock_id: '',
    product: '',
    quantity: '',
    note: '',
    destination: 'raw',
  });

  const isCrushingDest = (d) => d === 'crushing' || d === 'crushing_waste';

  // Transfer-out: один матеріал або суміш (пластик+барвник) одним кліком
  const handleReturnSubmit = async () => {
    setReturnError('');
    const destination = returnForm.destination || 'raw';
    const qty = Number(returnForm.quantity);

    if (returnForm.mode === 'mix') {
      if (!returnForm.product || !qty || qty <= 0) {
        setReturnError('Оберіть виріб і вкажіть загальну кількість суміші (кг)');
        return;
      }
      if (!isCrushingDest(destination) && destination !== 'disposal') {
        setReturnError('Суміш можна відправити на Склад перемолів (дробарку) або в утилізацію');
        return;
      }
      setReturnLoading(true);
      try {
        await api.post('/production-warehouse/transfer-out-mix/', {
          product: Number(returnForm.product),
          quantity: qty,
          note: returnForm.note,
          destination: destination === 'disposal' ? 'disposal' : 'crushing',
        });
        setReturnOpen(false);
        setReturnForm(emptyReturnForm());
        fetchStock();
        fetchWaste();
      } catch (err) {
        setReturnError(err.response?.data?.error || 'Помилка переміщення суміші');
      } finally {
        setReturnLoading(false);
      }
      return;
    }

    if (!returnForm.wh_stock_id || !qty || qty <= 0) {
      setReturnError('Оберіть матеріал та вкажіть кількість');
      return;
    }
    const selected = whStock.find((s) => s.id === Number(returnForm.wh_stock_id));
    if (isCrushingDest(destination) && selected?.material_type === 'packaging_material') {
      setReturnError('Упаковку не можна передати на дробарку');
      return;
    }
    setReturnLoading(true);
    try {
      const payload = {
        quantity: qty,
        note: returnForm.note,
        destination,
      };
      if (selected?.material_type === 'raw_material') payload.raw_material = selected.material_id;
      else if (selected?.material_type === 'dye') payload.dye = selected.material_id;
      else if (selected?.material_type === 'packaging_material') payload.packaging_material = selected.material_id;

      await api.post('/production-warehouse/transfer-out/', payload);
      setReturnOpen(false);
      setReturnForm(emptyReturnForm());
      fetchStock();
      if (isCrushingDest(destination)) fetchWaste();
    } catch (err) {
      setReturnError(err.response?.data?.error || 'Помилка при переміщенні');
    } finally {
      setReturnLoading(false);
    }
  };

  const fgGroups = useMemo(() => {
    const map = new Map();
    for (const r of receipts) {
      const orderKey = r.order == null ? 'null' : String(r.order);
      const key = `${orderKey}::${r.product}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          order: r.order ?? null,
          product: r.product,
          product_name: r.product_name || String(r.product),
          order_display: r.order_display || (r.order != null ? orderRefFromRow(r) : 'Вільний залишок'),
          total: 0,
          lines: [],
        });
      }
      const g = map.get(key);
      g.total += Number(r.quantity) || 0;
      g.lines.push(r);
    }
    const groups = Array.from(map.values());
    groups.forEach((g) => {
      g.lines.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return ta - tb;
      });
    });
    groups.sort((a, b) => String(a.order_display).localeCompare(String(b.order_display), 'uk'));
    return groups;
  }, [receipts]);

  const fgGrandTotal = useMemo(
    () => fgGroups.reduce((s, g) => s + (Number(g.total) || 0), 0),
    [fgGroups],
  );

  const openSgpDialog = ({ mode, group, receipt }) => {
    setSgpError('');
    if (mode === 'line' && receipt) {
      const available = Number(receipt.quantity) || 0;
      setSgpDialog({
        mode: 'line',
        order: receipt.order ?? null,
        product: receipt.product,
        product_name: receipt.product_name || String(receipt.product),
        order_display: receipt.order_display || (receipt.order != null ? orderRefFromRow(receipt) : 'Вільний залишок'),
        available,
        receiptId: receipt.id,
        lines: [receipt],
      });
      setSgpQty(String(available));
      return;
    }
    if (!group) return;
    setSgpDialog({
      mode: 'group',
      order: group.order,
      product: group.product,
      product_name: group.product_name,
      order_display: group.order_display,
      available: group.total,
      receiptId: null,
      lines: group.lines,
    });
    setSgpQty(String(group.total));
  };

  const handleConfirmSgpRelease = async () => {
    if (!sgpDialog || sgpLoading) return;
    const qty = parseInt(String(sgpQty).trim(), 10);
    if (!Number.isFinite(qty) || qty < 1) {
      setSgpError('Вкажіть кількість ≥ 1');
      return;
    }
    if (qty > sgpDialog.available) {
      setSgpError(`Не більше доступного: ${sgpDialog.available} шт`);
      return;
    }
    setSgpLoading(true);
    setSgpError('');
    setConfirmingId(sgpDialog.receiptId || sgpDialog.key || 'bulk');
    try {
      if (sgpDialog.mode === 'line' && sgpDialog.receiptId) {
        await api.post(`/finished-goods-receipts/${sgpDialog.receiptId}/release-to-finished/`, {
          quantity: qty,
        });
      } else {
        await api.post('/finished-goods-receipts/release-to-finished-bulk/', {
          order: sgpDialog.order,
          product: sgpDialog.product,
          quantity: qty,
        });
      }
      setSgpDialog(null);
      setSgpQty('');
      await fetchReceipts();
    } catch (err) {
      const d = err.response?.data;
      const msg =
        d?.quantity
          ? (Array.isArray(d.quantity) ? d.quantity.join(' ') : String(d.quantity))
          : (d?.detail || d?.error || err.userMessage || 'Помилка передачі на СГП');
      setSgpError(msg);
    } finally {
      setSgpLoading(false);
      setConfirmingId(null);
    }
  };

  // Send defect to crusher queue (A2) — часткова кількість у модалці
  const handleSendToWaste = async ({ quantity, defect }) => {
    setSendingWasteId(defect.id);
    try {
      await defectStockService.sendToWaste(defect.id, { quantity });
      setCrusherDialog(null);
      await fetchDefects();
      await fetchWaste();
    } finally {
      setSendingWasteId(null);
    }
  };

  // Create mixing batch return (Б2)
  const handleBatchSubmit = async () => {
    setBatchError('');
    if (!batchForm.product || !batchForm.quantity_kg) {
      setBatchError('Оберіть продукт та вкажіть кількість');
      return;
    }
    setBatchLoading(true);
    try {
      await api.post('/mixing-batch-stock/', {
        product: Number(batchForm.product),
        quantity_kg: Number(batchForm.quantity_kg),
        note: batchForm.note,
        composition: [],
      });
      setBatchOpen(false);
      setBatchForm({ product: '', quantity_kg: '', note: '' });
      fetchBatches();
    } catch (err) {
      setBatchError(err.response?.data?.error || 'Помилка при створенні запису');
    } finally {
      setBatchLoading(false);
    }
  };

  // Helpers
  const transferableStockItems = stockItems.filter(
    (si) => ['raw_material', 'dye', 'packaging'].includes(si.item_category)
  );

  const siName = (si) =>
    si.raw_material_name || si.dye_name || si.packaging_material_name || '—';

  const selectedStockItem = stockItems.find((si) => si.id === Number(transferForm.stock_item));
  const selectedWhStock = whStock.find((s) => s.id === Number(returnForm.wh_stock_id));

  const batchStatusLabel = (s) => ({ available: 'Доступний', used: 'Використано', cancelled: 'Скасовано' }[s] || s);
  const batchStatusColor = (s) => ({ available: 'success', used: 'default', cancelled: 'error' }[s] || 'default');

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Склад виробництва</Typography>
        <EntityImportExportButtons
          entityKey="production_warehouse_stock"
          label="Склад_виробництва"
          showImport={false}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        {visibleTabs.map((t) => (
          <Tab key={t.id} label={t.label} />
        ))}
      </Tabs>

      {/* ---- Materials ---- */}
      <TabPanel value={activeTabId} index="materials">
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setTransferError(''); setTransferOpen(true); }}
          >
            Прийняти зі складу
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            onClick={async () => {
              setReturnError('');
              setReturnForm(emptyReturnForm());
              setReturnOpen(true);
              if (!products.length) {
                try {
                  const prodRes = await api.get('/products/');
                  setProducts(prodRes.data?.results ?? prodRes.data ?? []);
                } catch (_) { /* ignore */ }
              }
            }}
          >
            Перемістити / вибути
          </Button>
          <Tooltip title="Оновити">
            <IconButton onClick={fetchStock} disabled={loadingStock}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loadingStock ? (
          <CircularProgress />
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Поточний залишок{stockTotal > 0 ? ` (${stockTotal})` : ''}
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 1, maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Матеріал</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell>Одиниця</TableCell>
                    <TableCell>Оновлено</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedWhStock.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                        Склад порожній
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedWhStock.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              row.material_type === 'raw_material' ? 'Сировина' :
                              row.material_type === 'dye' ? 'Барвник' :
                              row.material_type === 'packaging_material' ? 'Упаковка' : row.material_type
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {row.quantity < 1 && <WarningAmberIcon color="warning" fontSize="small" />}
                            {row.quantity}
                          </Box>
                        </TableCell>
                        <TableCell>{row.unit}</TableCell>
                        <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString('uk-UA') : '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {stockPages > 1 && (
              <Pagination
                count={stockPages}
                page={stockPage}
                onChange={(_, p) => setStockPage(p)}
                color="primary"
                size="small"
                sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}
              />
            )}
            {stockPages <= 1 && <Box sx={{ mb: 3 }} />}

            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Журнал переміщень{movementsTotal > 0 ? ` (${movementsTotal})` : ''}
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Матеріал</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell>Одиниця</TableCell>
                    <TableCell>Примітка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMovements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                        Немає записів
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedMovements.map((mv) => (
                      <TableRow key={mv.id} hover>
                        <TableCell>{new Date(mv.created_at).toLocaleString('uk-UA')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={mv.movement_type === 'in' ? 'success' : 'default'}
                            label={
                              mv.movement_type === 'in'
                                ? 'Прийом'
                                : (mv.note || '').toLowerCase().includes('дробарк')
                                  ? 'На дробарку'
                                  : 'Повернення'
                            }
                          />
                        </TableCell>
                        <TableCell>{mv.name}</TableCell>
                        <TableCell align="right">{mv.quantity}</TableCell>
                        <TableCell>{mv.unit}</TableCell>
                        <TableCell>{mv.note || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {movementsPages > 1 && (
              <Pagination
                count={movementsPages}
                page={movementsPage}
                onChange={(_, p) => setMovementsPage(p)}
                color="primary"
                size="small"
                sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}
              />
            )}
          </>
        )}

        {/* Transfer-in dialog */}
        <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Прийняти матеріал зі складу</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {transferError && <Alert severity="error" sx={{ mb: 2 }}>{transferError}</Alert>}
            <TextField
              select
              fullWidth
              label="Матеріал"
              value={transferForm.stock_item}
              onChange={(e) => setTransferForm((f) => ({ ...f, stock_item: e.target.value, quantity: '' }))}
              sx={{ mb: 2 }}
            >
              {transferableStockItems.map((si) => (
                <MenuItem key={si.id} value={si.id}>
                  {siName(si)} — {si.quantity} {si.unit}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Кількість"
              value={transferForm.quantity}
              onChange={(e) => setTransferForm((f) => ({ ...f, quantity: e.target.value }))}
              inputProps={{ min: 0.001, step: 0.001 }}
              helperText={
                selectedStockItem
                  ? `Доступно на складі: ${selectedStockItem.available_quantity ?? selectedStockItem.quantity} ${selectedStockItem.unit}`
                  : ''
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTransferOpen(false)}>Скасувати</Button>
            <Button variant="contained" onClick={handleTransferSubmit} disabled={transferLoading}>
              {transferLoading ? <CircularProgress size={20} /> : 'Прийняти'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transfer-out: single material or mixture */}
        <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Переміщення зі складу виробництва</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {returnError && <Alert severity="error" sx={{ mb: 2 }}>{returnError}</Alert>}
            <TextField
              select
              fullWidth
              label="Тип"
              value={returnForm.mode || 'single'}
              onChange={(e) => setReturnForm((f) => ({
                ...f,
                mode: e.target.value,
                wh_stock_id: '',
                product: '',
                quantity: '',
                destination: e.target.value === 'mix'
                  ? 'crushing_waste'
                  : (f.destination === 'disposal' ? 'raw' : f.destination),
              }))}
              sx={{ mb: 2 }}
              helperText={
                returnForm.mode === 'mix'
                  ? 'Змішана сировина (пластик + барвник) — одне списання, одна партія на дробарку'
                  : 'Окремий матеріал, як раніше'
              }
            >
              <MenuItem value="single">Один матеріал</MenuItem>
              <MenuItem value="mix">Суміш (пластик + барвник)</MenuItem>
            </TextField>

            {returnForm.mode === 'mix' ? (
              <TextField
                select
                fullWidth
                label="Виріб (рецепт суміші)"
                value={returnForm.product}
                onChange={(e) => setReturnForm((f) => ({ ...f, product: e.target.value }))}
                sx={{ mb: 2 }}
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                select
                fullWidth
                label="Матеріал (зі складу виробництва)"
                value={returnForm.wh_stock_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const s = whStock.find((x) => String(x.id) === String(id));
                  setReturnForm((f) => ({
                    ...f,
                    wh_stock_id: id,
                    quantity: s && Number(s.quantity) > 0 ? String(s.quantity) : '',
                  }));
                }}
                sx={{ mb: 2 }}
              >
                {whStock.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} — {s.quantity} {s.unit}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              select
              fullWidth
              label="Куди переміщуємо"
              value={returnForm.destination || 'raw'}
              onChange={(e) => setReturnForm((f) => ({ ...f, destination: e.target.value }))}
              sx={{ mb: 2 }}
              helperText={
                isCrushingDest(returnForm.destination)
                  ? 'Вага спишеться зі СВ і зʼявиться в черзі «Нові» на дробарці'
                  : returnForm.destination === 'disposal'
                    ? 'Списання як невідновлювальні відходи (утилізація)'
                    : 'Повернення на обраний склад (як на основному складі)'
              }
            >
              {returnForm.mode === 'mix' ? (
                <>
                  <MenuItem value="crushing_waste">Склад перемолів (дробарка)</MenuItem>
                  <MenuItem value="disposal">Утилізація</MenuItem>
                </>
              ) : (
                transferWarehouses.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))
              )}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label={returnForm.mode === 'mix' ? 'Загальна кількість суміші, кг' : 'Кількість'}
              value={returnForm.quantity}
              onChange={(e) => setReturnForm((f) => ({ ...f, quantity: e.target.value }))}
              inputProps={{ min: 0.001, step: 0.001 }}
              helperText={
                returnForm.mode === 'mix'
                  ? 'Компоненти спишуться пропорційно техкарті виробу'
                  : (selectedWhStock
                    ? `Наявно на СВ: ${selectedWhStock.quantity} ${selectedWhStock.unit}`
                    : '')
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Примітка"
              value={returnForm.note}
              onChange={(e) => setReturnForm((f) => ({ ...f, note: e.target.value }))}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReturnOpen(false)}>Скасувати</Button>
            <Button
              variant="contained"
              color={isCrushingDest(returnForm.destination) || returnForm.destination === 'disposal' ? 'error' : 'warning'}
              onClick={handleReturnSubmit}
              disabled={returnLoading}
            >
              {returnLoading
                ? <CircularProgress size={20} />
                : (isCrushingDest(returnForm.destination)
                  ? 'На дробарку'
                  : returnForm.destination === 'disposal'
                    ? 'Утилізувати'
                    : 'Перемістити')}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ---- TAB 1: Finished goods ---- */}
      <TabPanel value={activeTabId} index="finished">
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Оновити">
            <IconButton onClick={fetchReceipts} disabled={loadingReceipts}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!loadingReceipts && receipts.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Разом на складі виробництва: <strong>{fgGrandTotal}</strong> шт
              {' · '}
              груп: {fgGroups.length}
            </Typography>
          ) : null}
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Рядки згруповано за замовленням і виробом. «Передати на СГП» можна на всю групу
          (система назбирає з дат FIFO) або по окремому рядку — з вказанням кількості.
        </Alert>

        {loadingReceipts ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell>Замовлення</TableCell>
                  <TableCell>Продукт</TableCell>
                  <TableCell align="right">Разом, шт</TableCell>
                  <TableCell align="right">Рядків</TableCell>
                  <TableCell align="center">Дія</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fgGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                      Немає продукції, що очікує передачі на СГП
                    </TableCell>
                  </TableRow>
                ) : (
                  fgGroups.map((g) => {
                    const open = Boolean(fgExpand[g.key]);
                    return (
                      <React.Fragment key={g.key}>
                        <TableRow hover sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => setFgExpand((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                            >
                              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{g.order_display}</TableCell>
                          <TableCell>{g.product_name}</TableCell>
                          <TableCell align="right"><strong>{g.total}</strong></TableCell>
                          <TableCell align="right">{g.lines.length}</TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              disabled={Boolean(confirmingId) || sgpLoading}
                              onClick={() => openSgpDialog({ mode: 'group', group: g })}
                            >
                              Передати на СГП
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, pl: 2 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Дата</TableCell>
                                      <TableCell align="right">Кількість</TableCell>
                                      <TableCell>Склад</TableCell>
                                      <TableCell align="center">Дія</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {g.lines.map((r) => (
                                      <TableRow key={r.id}>
                                        <TableCell>
                                          {r.created_at
                                            ? new Date(r.created_at).toLocaleString('uk-UA')
                                            : '—'}
                                        </TableCell>
                                        <TableCell align="right">{r.quantity}</TableCell>
                                        <TableCell>{r.warehouse || '—'}</TableCell>
                                        <TableCell align="center">
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="success"
                                            disabled={Boolean(confirmingId) || sgpLoading}
                                            onClick={() => openSgpDialog({ mode: 'line', receipt: r })}
                                          >
                                            Передати…
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={Boolean(sgpDialog)}
          onClose={() => { if (!sgpLoading) { setSgpDialog(null); setSgpError(''); } }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Передати на СГП</DialogTitle>
          <DialogContent>
            {sgpError ? <Alert severity="error" sx={{ mb: 2 }}>{sgpError}</Alert> : null}
            {sgpDialog ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                <Typography variant="body2">
                  <strong>Замовлення:</strong> {sgpDialog.order_display}
                </Typography>
                <Typography variant="body2">
                  <strong>Виріб:</strong> {sgpDialog.product_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Доступно: {sgpDialog.available} шт
                  {sgpDialog.mode === 'group'
                    ? ` (${sgpDialog.lines?.length || 0} рядків, FIFO зі старіших)`
                    : ' (один рядок)'}
                </Typography>
                <TextField
                  label="Скільки перемістити, шт"
                  type="number"
                  value={sgpQty}
                  onChange={(e) => setSgpQty(e.target.value)}
                  fullWidth
                  inputProps={{ min: 1, max: sgpDialog.available, step: 1 }}
                  helperText="За замовчуванням — уся доступна кількість"
                />
                <Alert severity="warning">
                  Після підтвердження {sgpQty || '…'} шт піде в «Надходження» СГП
                  (очікує підтвердження комірником).
                </Alert>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSgpDialog(null)} disabled={sgpLoading}>Скасувати</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmSgpRelease}
              disabled={sgpLoading}
              startIcon={sgpLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            >
              Підтвердити
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ---- TAB 2: Defects / Брак (A2) ---- */}
      <TabPanel value={activeTabId} index="defect">
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Tooltip title="Оновити">
            <IconButton onClick={fetchDefects} disabled={loadingDefects}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Бракована продукція надходить автоматично з операторських звітів. Відправте на дробарку (можна частково) — партія зʼявиться в черзі «Нові»; решта лишається на складі.
        </Alert>

        <SendToCrusherDialog
          open={Boolean(crusherDialog)}
          defect={crusherDialog}
          onClose={() => !sendingWasteId && setCrusherDialog(null)}
          onSubmit={handleSendToWaste}
        />

        {loadingDefects ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Замовлення</TableCell>
                  <TableCell>Продукт</TableCell>
                  <TableCell align="right">Кількість, шт</TableCell>
                  <TableCell align="right">Вага, кг</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell align="center">Дія</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {defects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                      Немає браку на складі виробництва
                    </TableCell>
                  </TableRow>
                ) : (
                  defects.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell>{d.order_display || orderRefFromRow(d)}</TableCell>
                      <TableCell>{d.product_name || d.product}</TableCell>
                      <TableCell align="right">{d.quantity}</TableCell>
                      <TableCell align="right">{d.weight_kg ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={d.status === 'pending' ? 'warning' : 'default'}
                          label={d.status === 'pending' ? 'Очікує' : 'Відправлено'}
                        />
                      </TableCell>
                      <TableCell>{d.created_at ? new Date(d.created_at).toLocaleString('uk-UA') : '—'}</TableCell>
                      <TableCell align="center">
                        {d.status === 'pending' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={sendingWasteId === d.id ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
                            disabled={sendingWasteId === d.id}
                            onClick={() => setCrusherDialog(d)}
                          >
                            На дробарку
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ---- TAB 3: Mixing Batches / Замісне тісто (Б2) ---- */}
      <TabPanel value={activeTabId} index="mixing">
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setBatchError(''); setBatchOpen(true); }}
          >
            Повернути заміс
          </Button>
          <Tooltip title="Оновити">
            <IconButton onClick={fetchBatches} disabled={loadingBatches}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loadingBatches ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Продукт</TableCell>
                  <TableCell align="right">Кількість, кг</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Примітка</TableCell>
                  <TableCell>Дата повернення</TableCell>
                  <TableCell>Використано в замовленні</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                      Немає замісів
                    </TableCell>
                  </TableRow>
                ) : (
                  batches.map((b) => (
                    <TableRow key={b.id} hover>
                      <TableCell>{b.product_name || b.product}</TableCell>
                      <TableCell align="right">{b.quantity_kg}</TableCell>
                      <TableCell>
                        <Chip size="small" color={batchStatusColor(b.status)} label={batchStatusLabel(b.status)} />
                      </TableCell>
                      <TableCell>{b.note || '—'}</TableCell>
                      <TableCell>{b.created_at ? new Date(b.created_at).toLocaleString('uk-UA') : '—'}</TableCell>
                      <TableCell>{b.used_in_order_display || b.used_in_order_ref_display || (b.used_in_order ? orderRefFromRow({ production_order_id: b.used_in_order, customer_order_number: b.used_in_customer_order_number }) : '—')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create batch return dialog */}
        <Dialog open={batchOpen} onClose={() => setBatchOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Повернути заміс на СВ</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {batchError && <Alert severity="error" sx={{ mb: 2 }}>{batchError}</Alert>}
            <TextField
              select
              fullWidth
              label="Продукт"
              value={batchForm.product}
              onChange={(e) => setBatchForm((f) => ({ ...f, product: e.target.value }))}
              sx={{ mb: 2 }}
            >
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label="Кількість, кг"
              value={batchForm.quantity_kg}
              onChange={(e) => setBatchForm((f) => ({ ...f, quantity_kg: e.target.value }))}
              inputProps={{ min: 0.001, step: 0.001 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Примітка"
              value={batchForm.note}
              onChange={(e) => setBatchForm((f) => ({ ...f, note: e.target.value }))}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBatchOpen(false)}>Скасувати</Button>
            <Button variant="contained" onClick={handleBatchSubmit} disabled={batchLoading}>
              {batchLoading ? <CircularProgress size={20} /> : 'Зберегти'}
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ---- TAB 4: Waste lots / Відходи ---- */}
      <TabPanel value={activeTabId} index="waste">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Tooltip title="Оновити"><IconButton onClick={fetchWaste} disabled={loadingWaste}><RefreshIcon /></IconButton></Tooltip>
        </Box>
        {loadingWaste ? <CircularProgress /> : (
          <>
            {/* ── Відновлювальні ── */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 1 }}>
              ♻ Відновлювальні відходи → черга «Дроблення»
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Партії зʼявляються в меню «Дроблення» (статус «Нові»). На склад перемолів потрапляють лише після
              завершення дроблення з фактичною вихідною вагою.
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Замовлення</TableCell>
                    <TableCell>Перемол (склад)</TableCell>
                    <TableCell align="right">План (кг)</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Дата</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wasteLots.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>Немає відходів у черзі</TableCell></TableRow>
                  ) : wasteLots.map((lot) => (
                    <TableRow key={lot.id} hover>
                      <TableCell>
                        {lot.production_order_id || lot.production_order
                          ? orderRefFromRow(lot)
                          : 'СВ (без замовлення)'}
                      </TableCell>
                      <TableCell>{lot.peremol_name || lot.product_name}</TableCell>
                      <TableCell align="right">{Number(lot.quantity_initial).toFixed(3)} кг</TableCell>
                      <TableCell>
                        <Chip size="small" label={lot.board_status_display || 'Нові'} color="info" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{lot.created_at ? new Date(lot.created_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Невідновлювальні (з журналу) ── */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: '#c62828', display: 'flex', alignItems: 'center', gap: 1 }}>
              🗑 Невідновлювальні відходи (утилізовано)
            </Typography>
            {(() => {
              const nonRec = wasteMovements.filter(
                (m) => m.destination === 'disposal'
                  || (m.note && String(m.note).toLowerCase().includes('утилізація')),
              );
              return (
                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата</TableCell>
                        <TableCell>Матеріал / суміш</TableCell>
                        <TableCell align="right">Кількість</TableCell>
                        <TableCell>Примітка</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {nonRec.length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>Немає утилізованих відходів</TableCell></TableRow>
                      ) : nonRec.map((m) => (
                        <TableRow key={m.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.created_at ? new Date(m.created_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                          <TableCell>{m.display_name || m.name}</TableCell>
                          <TableCell align="right">{Number(m.quantity).toFixed(4)} {m.unit || 'кг'}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{m.note}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()}

            {/* ── Журнал всіх рухів відходів (згруповано по сумішах) ── */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Журнал переміщень відходів</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Суміш (пластик + барвник) показується одним рядком; склад можна розгорнути. Показано до 200 останніх рухів.
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Дата</TableCell>
                    <TableCell>Матеріал / суміш</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell align="right">Кількість</TableCell>
                    <TableCell>Замовлення</TableCell>
                    <TableCell>Примітка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wasteMovements.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>Немає записів</TableCell></TableRow>
                  ) : wasteMovements.map((m) => {
                    const rowId = String(m.id);
                    const isMix = Boolean(m.is_mix) && (m.components || []).length > 1;
                    const expanded = wasteExpandedId === rowId;
                    const typeLabel = m.material_type === 'mix' || isMix
                      ? 'Суміш'
                      : m.material_type === 'raw_material'
                        ? 'Сировина'
                        : m.material_type === 'dye'
                          ? 'Барвник'
                          : m.material_type === 'packaging'
                            ? 'Упаковка'
                            : '—';
                    return (
                      <React.Fragment key={rowId}>
                        <TableRow hover>
                          <TableCell sx={{ py: 0.5 }}>
                            {isMix ? (
                              <IconButton
                                size="small"
                                onClick={() => setWasteExpandedId(expanded ? null : rowId)}
                                aria-label={expanded ? 'Згорнути склад' : 'Показати склад'}
                              >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {m.created_at
                              ? new Date(m.created_at).toLocaleString('uk-UA', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                              })
                              : '—'}
                          </TableCell>
                          <TableCell>{m.display_name || m.name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={isMix ? 'primary' : 'default'}
                              label={typeLabel}
                              sx={{ fontWeight: isMix ? 600 : 400 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {Number(m.quantity).toFixed(4)} {m.unit || 'кг'}
                          </TableCell>
                          <TableCell>
                            {m.order_ref_display || orderRefFromRow(m)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem', maxWidth: 260 }}>
                            {m.note_display || m.note}
                            {m.crushing_lot_id ? ` · партія #${m.crushing_lot_id}` : ''}
                          </TableCell>
                        </TableRow>
                        {isMix && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 1, px: 2, bgcolor: 'action.hover' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Склад суміші (пропорції під капотом)
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Компонент</TableCell>
                                        <TableCell>Вид</TableCell>
                                        <TableCell align="right">Кількість</TableCell>
                                        <TableCell align="right">%</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(m.components || []).map((c) => (
                                        <TableRow key={c.movement_id || `${c.kind}-${c.name}`}>
                                          <TableCell>{c.name}</TableCell>
                                          <TableCell>
                                            {c.kind === 'dye' ? 'Барвник' : c.kind === 'raw_material' ? 'Сировина' : c.kind || '—'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {Number(c.quantity).toFixed(4)} {c.unit || 'кг'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {c.percentage != null ? `${Number(c.percentage).toFixed(2)}%` : '—'}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>
    </Box>
  );
}
